import type { App } from "vue";
import { FeatureFlagService } from "../services/FeatureFlagService";
import { FEATURE_FLAG_SERVICE_KEY } from "../utils/symbols";
import { featureFlagDirective } from "../directives/featureFlag";
import type { FeatureFlagConfig } from "../types";

export interface FlagpolePluginOptions extends FeatureFlagConfig {
  // Plugin-specific options can be added here
}

/**
 * Vue plugin for Flagpole feature flags.
 *
 * @example
 * // main.ts
 * import { createApp } from 'vue';
 * import { createFlagpole } from '@flagpole/client-vue';
 *
 * const app = createApp(App);
 *
 * app.use(createFlagpole({
 *   apiKey: 'your-api-key',
 *   environments: ['development']
 * }));
 *
 * app.mount('#app');
 */
export function createFlagpole(options: FlagpolePluginOptions) {
  return {
    install(app: App) {
      // Create service instance
      const service = new FeatureFlagService(options);

      // Provide service for injection
      app.provide(FEATURE_FLAG_SERVICE_KEY, service);

      // Add to global properties for directive access
      app.config.globalProperties.$flagpoleService = service;

      // Register directive
      app.directive("feature-flag", featureFlagDirective);

      // Global method for checking flags
      app.config.globalProperties.$isFeatureEnabled = (
        flagName: string
      ): boolean => {
        return service.isFeatureEnabled(flagName);
      };

      // Cleanup on app unmount
      const originalUnmount = app.unmount;
      app.unmount = function () {
        service.destroy();
        originalUnmount.call(this);
      };
    },
  };
}

// Type augmentation for global properties
declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    $flagpoleService: FeatureFlagService;
    $isFeatureEnabled: (flagName: string) => boolean;
  }
}
